/**
 * Named zone abbreviations the backend may already suffix onto a date
 * string (North American zones, plus UTC/GMT). Kept as an explicit
 * whitelist rather than "any trailing letters" — a broad `[A-Za-z]{2,5}$`
 * match would also swallow ordinary trailing text such as an `AM`/`PM`
 * marker, silently skipping the offset append below and parsing the value
 * as local time instead of UTC.
 */
const TZ_ABBREVIATIONS = [
  'UTC',
  'GMT',
  'EST',
  'EDT',
  'CST',
  'CDT',
  'MST',
  'MDT',
  'PST',
  'PDT',
  'AKST',
  'AKDT',
  'HST',
  'HDT',
  'AST',
  'ADT',
];

/**
 * Detects whether a date string already carries timezone info: a
 * zero-padded numeric offset (`Z`, `+05:00`, `-0500`), a named zone
 * abbreviation (e.g. `CDT`, `EST`), or a `GMT`/`UTC`-relative offset as
 * produced by `Intl.DateTimeFormat(..., { timeZoneName: 'short' })` for
 * non-North-American zones (e.g. `GMT+9`, `GMT+5:30`, `UTC-3`) — these use
 * an *unpadded* hour, so they don't match the numeric-offset pattern above.
 * Backend/display values for some grids (e.g. Outcome Documents) arrive
 * pre-formatted with a trailing zone marker instead of a numeric offset;
 * treating only `Z`/`+`/`-` as "has an offset" caused a bare `Z` to be
 * appended after the marker (e.g. `...CDTZ`), which `Date`/`DatePipe`
 * cannot parse.
 */
function hasTimezoneInfo(isoString: string): boolean {
  const trimmed = isoString.trim();
  const abbreviationPattern = TZ_ABBREVIATIONS.join('|');
  const pattern = new RegExp(
    `(?:Z|[+-]\\d{2}:?\\d{2}|\\b(?:${abbreviationPattern})\\b|\\b(?:GMT|UTC)[+-]\\d{1,2}(?::?\\d{2})?)$`,
    'i',
  );
  return pattern.test(trimmed);
}

export function appendPacificOffset(isoString: string) {
  // Check if string already has an offset
  if (hasTimezoneInfo(isoString)) {
    return isoString;
  }

  // Get current Pacific offset
  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return 'N/A';
  }

  // Get Pacific time offset
  const pacificDate = new Date(
    date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
    }),
  );
  const utcDate = new Date(
    date.toLocaleString('en-US', {
      timeZone: 'UTC',
    }),
  );

  // Calculate offset in hours
  const offsetHours =
    (pacificDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);

  // Format the offset as -07:00 or -08:00
  const offsetStr =
    offsetHours <= -10 ? `${offsetHours}:00` : `-0${-offsetHours}:00`;

  return `${isoString}${offsetStr}`;
}

export function appendUTCOffset(isoString: string) {
  // Check if string already has an offset
  if (hasTimezoneInfo(isoString)) {
    return isoString;
  }

  const withOffset = `${isoString}Z`;

  // Guard against producing a string DatePipe can't parse; fall back to the
  // original value so a bad date never throws downstream.
  return isNaN(new Date(withOffset).getTime()) ? isoString : withOffset;
}
